/**
 * Live creative-fill progress in the board header: the pill (fill/FillPill)
 * while a fill runs, waits for the AI, stalled, or finished with drafts
 * that failed twice (20/10/2026 — it used to vanish or spin forever). The
 * completion/failure toast fires exactly once on the FILLING -> terminal
 * edge (the seed-job idiom) so reloads and old runs never re-toast; the
 * same edge reads the errors again (21/10/2026 — the ones it fixed show
 * Resolved at once).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useEffect, useRef, useState } from 'react';

import type { DropGenCreativeFillStatus } from '@trafficpulse/shared';

import { useToast } from '@/providers/ToastProvider';

import { useCreativeFillStatus } from '../../useDropGenerator';
import { useRefreshErrors } from '../errors-tab/use-refresh-errors';
import { useCountryClock } from '../shared/time/country-clock-context';
import { useMinuteTick } from './facts/use-minute-tick';
import { pillModel } from './fill/fill-words';
import { FillPill } from './fill/FillPill';

const doneDescription = (s: DropGenCreativeFillStatus): string => {
  const failed = s.report?.reduce((n, r) => n + r.failed, 0) ?? 0;
  const skipped = s.report?.filter((r) => r.skippedReason).length ?? 0;
  const reused = s.report?.reduce((n, r) => n + (r.reused ?? 0), 0) ?? 0;
  const parts = [`${s.filled} drafts ready to review`];
  if (reused > 0) parts.push(`${reused} with proven texts`);
  if (failed > 0) parts.push(`${failed} failed`);
  if (skipped > 0) parts.push(`${skipped} container${skipped === 1 ? '' : 's'} skipped`);
  return parts.join(' · ');
};

export const CreativeFillIndicator: FC<{ countryKey: string }> = ({ countryKey }) => {
  const status = useCreativeFillStatus(countryKey);
  const { success, error } = useToast();
  const refreshErrors = useRefreshErrors();
  const wasFilling = useRef(false);
  const [dismissed, setDismissed] = useState<string | null>(null); // the failed pill hidden, until the next fill
  const clock = useCountryClock();
  const now = useMinuteTick();
  const s = status.data;
  useEffect(() => {
    if (s?.status === 'FILLING') {
      wasFilling.current = true;
      return;
    }
    if (!wasFilling.current || !s) return;
    wasFilling.current = false;
    refreshErrors();
    if (s.status === 'DONE') success('Texts filled', doneDescription(s));
    else if (s.status === 'FAILED') error('Creative fill failed', s.error ?? undefined);
  }, [s, success, error, refreshErrors]);
  const model = pillModel(s, now, clock);
  if (!model || (model.tone === 'failed' && dismissed === s?.updatedAt)) return null;
  const pct = s && s.total > 0 ? Math.max(4, Math.round((s.filled / s.total) * 100)) : 4;
  return <FillPill model={model} countryKey={countryKey} pct={pct} onDismiss={model.tone === 'failed' ? () => setDismissed(s?.updatedAt ?? null) : undefined} />;
};
